import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Slide
} from '@mui/material';
import { motion } from 'framer-motion';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import { Snackbar, Alert } from '@mui/material';

const AddQuestionDialogBox = ({ open, handleClose, data }) => {
  const [saving, setSaving] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [formData, setFormData] = useState({
    question: '',
    mimeType: 'Text',
    domain_id: '',
    active: 1,
    keywords: '',
    hint: '',
    explanation: '',
    answer: Array(4).fill({ answer: '', is_correct: 0 }),
  });
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

const showSnack = (msg, severity = 'success') => {
  setSnack({ open: true, msg, severity });
};

  useEffect(() => {
    if (data?.domains?.length) {
      const defaultDomainId = data.domains[0].domainId;
      setSelectedDomainId(defaultDomainId);
      setFormData((prev) => ({ ...prev, domain_id: defaultDomainId }));
    }
  }, [data]);

  const handleDomainChange = (e) => {
    const domainId = e.target.value;
    setSelectedDomainId(domainId);
    setFormData((prev) => ({ ...prev, domain_id: domainId }));
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleAnswerChange = (index, value) => {
    const updatedAnswers = [...formData.answer];
    updatedAnswers[index] = { ...updatedAnswers[index], answer: value };
    setFormData((prev) => ({ ...prev, answer: updatedAnswers }));
  };

  const handleCorrectChange = (index) => {
    const updatedAnswers = formData.answer.map((ans, i) => ({
      ...ans,
      is_correct: i === index ? 1 : 0,
    }));
    setFormData((prev) => ({ ...prev, answer: updatedAnswers }));
  };

  const getBasicAuthHeader = () => {
        const username = process.env.REACT_APP_API_USERNAME;
        const password = process.env.REACT_APP_API_PASSWORD;
        const encodedCredentials = btoa(`${username}:${password}`);
        return `Basic ${encodedCredentials}`;
      };

  const handleSubmit = async () => {
  if (!formData.question.trim() || formData.answer.every(ans => !ans.answer.trim())) {
    alert("Please enter a question and at least one answer.");
    return;
  }

  const payload = [formData];
  setSaving(true);

  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/generate_trainingquestion/${data.trainingId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
        'Authorization': getBasicAuthHeader(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to save: ${response.statusText}`);
    }

    showSnack('✅ Question saved successfully!');
  } catch (error) {
    console.error('Error saving question:', error);
    showSnack('❌ Failed to save question.', 'error');
  } finally {
    setSaving(false);
    handleClose();
  }
};

  if (!data?.domains) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up' }}
      PaperProps={{
        component: motion.div,
        initial: { scale: 0.95, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        exit: { scale: 0.95, opacity: 0 },
        transition: { duration: 0.4 },
      }}
    >
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
  <Alert severity={snack.severity}>{snack.msg}</Alert>
</Snackbar>
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold" color="primary">
          ✨ Create a New Question
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3} p={1}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Domain</InputLabel>
            <Select
              value={selectedDomainId}
              label="Select Domain"
              onChange={handleDomainChange}
              sx={{ borderRadius: 2 }}
            >
              {(data.domains || []).map((domain) => (
                <MenuItem key={domain.domainId} value={domain.domainId}>
                  {domain.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Question"
            variant="outlined"
            fullWidth
            value={formData.question}
            onChange={handleChange('question')}
            sx={{ borderRadius: 2 }}
          />

          <TextField
            label="Keywords"
            fullWidth
            value={formData.keywords}
            onChange={handleChange('keywords')}
          />

          <TextField
            label="Hint"
            fullWidth
            value={formData.hint}
            onChange={handleChange('hint')}
          />

          <TextField
            label="Explanation"
            fullWidth
            value={formData.explanation}
            onChange={handleChange('explanation')}
            multiline
            rows={3}
          />

          <Box>
            <Typography variant="h6" gutterBottom>
              📝 Answer Options
            </Typography>
            {formData.answer.map((ans, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                gap={2}
                mt={1}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 1,
                }}
              >
                <TextField
                  label={`Answer ${index + 1}`}
                  fullWidth
                  value={ans.answer}
                  onChange={(e) => handleAnswerChange(index, e.target.value)}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ans.is_correct === 1}
                      onChange={() => handleCorrectChange(index)}
                      color="success"
                    />
                  }
                  label="Correct"
                />
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <ButtonSpinnerSlot size={24} /> : "🚀 Save Question"}
        </Button>
        <Button variant="outlined" onClick={handleClose}>
          ❌ Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddQuestionDialogBox;
